import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { QK, queryKeys } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";
import { invalidateOperationsDashboard } from "./useOperationsDashboard";

interface ReextractUploadResult {
  reextracted: boolean;
  project_id: string;
  upload_id: string;
  status: string | null;
}

// Re-run extraction for one upload against the bytes already in S3 (no re-pull
// from the source, so a Gmail thread is re-extracted without a Gmail call). The
// POST queues it on the extraction queue and flips the row to "pending"; there is
// no per-file operation to poll. Every surface already polls the row's
// extraction_status through its own list query while it is pending or running
// (principle 31), so this hook only refreshes those queries once the row has
// flipped. A re-click while it is in flight is a no-op server-side.
export const useReextractUpload = (projectId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  const [activeUploadId, setActiveUploadId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const triggerMutation = useMutation<ReextractUploadResult, Error, string>(
    (uploadId: string) =>
      http<ReextractUploadResult>("/uploads/reextract", { upload_id: uploadId }),
  );

  const reextract = useCallback(
    async (uploadId: string) => {
      setError(null);
      setActiveUploadId(uploadId);
      try {
        await triggerMutation.mutateAsync(uploadId);
        qc.invalidateQueries({ queryKey: queryKeys.uploadsTree(projectId) });
        qc.invalidateQueries({ queryKey: queryKeys.uploadsList(projectId) });
        qc.invalidateQueries({ queryKey: queryKeys.uploadExtracted(uploadId) });
        // Prefix-only so it matches whatever id set the Files page has mounted.
        qc.invalidateQueries({ queryKey: [QK.uploadsExtractedBatch] });
        invalidateOperationsDashboard(qc);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setActiveUploadId(null);
      }
    },
    [triggerMutation, qc, projectId],
  );

  const clearError = useCallback(() => setError(null), []);

  // ``inFlightUploadId`` lets a caller disable / relabel only the row being
  // queued, not every row at once.
  return {
    reextract,
    inFlightUploadId: activeUploadId,
    isInFlight: activeUploadId !== null,
    error,
    clearError,
  };
};
